/**
 * Library — unified content hub.
 *
 * Mine tab: rebuilt Gallery v2 design (mockups 4a/4b, states 3a/3b).
 *   - Single vertical deck list; no Decks|Courses secondary tab (user-created
 *     courses are retired — courses live in Catalog as system-only content).
 *   - Deck cards compose from shared primitives: DeckCover, DeckStatusBadge,
 *     DeckProgressBar.
 *   - Primary "+" FAB opens AddContentSheet (shared with HomePage; consolidated
 *     entry points minus "find decks", which duplicates this page's own Catalog tab).
 *   - Search + overflow (sort, multi-select) replaces the old scattered toolbar.
 *   - Folders chip drives folder-scoped filtering.
 *
 * Catalog tab delegates to its existing page component unchanged.
 */
import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  CheckSquare,
  ChevronDown,
  Folder,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Square,
  X,
} from 'lucide-react'

import DeckCard from '../components/shared/DeckCard'
import DeckProgressBar from '../components/shared/DeckProgressBar'
import DeckStatusBadge from '../components/shared/DeckStatusBadge'
import AddContentSheet from '../components/shared/AddContentSheet'
import CSVImportModal from '../components/shared/CSVImportModal'
import DeckCreateModal from '../components/DeckCreateModal'
import FolderManagementModal from '../components/FolderManagementModal'
import GroupIntoCourseSheet from '../components/GroupIntoCourseSheet'
import TextList from '../components/texts/TextList'
import { Segmented } from '../components/texts/ui'
import { useTextList } from '../hooks/useTexts'
import { useMineSegment, type MineSegment } from '../stores/mineSegment'
import { useTextFlowStore } from '../stores/textFlowStore'
import { CONTROL_SURFACE } from '../utils/controlSurface'
import { toast } from '../components/shared/Toast'
import { extractErrorMessage } from '../api/client'

import {
  useDecks,
  useCreateDeck,
  useDeleteDeck,
  useUpdateDeck,
} from '../hooks/useDecks'
import { useFolders } from '../hooks/useFolders'
import { useFlipMenuPlacement } from '../hooks/useFlipMenuPlacement'
import { useScreenHeader } from '../hooks/useScreenHeader'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import { useExclusiveOverlay } from '../hooks/useExclusiveOverlay'
import { APP_SCROLL_ID } from '../utils/appScroll'
import { SCREEN_HEADER_GAP } from '../utils/screenHeader'

import CatalogPage from './CatalogPage'
import { resolveLibraryAddress, type CatalogSub } from '../utils/libraryAddress'
import { scrollAppToTop } from '../utils/appScroll'
import { useStopScroll } from '../hooks/useStopScroll'
import {
  hasStopMemory, patchStopState, readStopMemory, stopState, writeCatalogSub,
} from '../stores/stopMemory'

import { useLocale } from '../i18n/context'

import type { Deck, DeckCreate, Folder as FolderType } from '../types'

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 40          // larger page keeps sort meaningful on one request
const LONG_PRESS_MS = 450
// How far a touch may wander, on either axis, before a pending long-press is
// cancelled — the deck screen's card long-press tolerance (`VocabCard`'s
// LONG_PRESS_JITTER), so the app's two long-presses behave alike.
const LONG_PRESS_MOVE_TOLERANCE_PX = 10
// Tailwind's `md` breakpoint as a media query — the width at which the
// `md:` classes in this file switch.
const MD_BREAKPOINT_QUERY = '(min-width: 768px)'

const SORT_KEYS = ['newest', 'oldest', 'az', 'za', 'most-due'] as const
type SortKey = (typeof SORT_KEYS)[number]

function sortDecks(decks: Deck[], key: SortKey): Deck[] {
  const arr = [...decks]
  switch (key) {
    case 'oldest':
      return arr.sort((a, b) => a.created_at.localeCompare(b.created_at))
    case 'az':
      return arr.sort((a, b) => a.title.localeCompare(b.title))
    case 'za':
      return arr.sort((a, b) => b.title.localeCompare(a.title))
    case 'most-due':
      return arr.sort((a, b) => (b.due_count ?? 0) - (a.due_count ?? 0))
    default: // 'newest' — backend already returns newest first
      return arr
  }
}

// ── Segment routing ───────────────────────────────────────────────────────────

type Seg = 'mine' | 'catalog'

export default function LibraryPage() {
  const [params, setParams] = useSearchParams()
  const { tr } = useLocale()
  const lib = tr.library
  // Opening the library with no ?seg= at all lands on the reader's own
  // decks — Catalog is one deliberate tap away, not the landing surface.
  // (No decision record covers this default specifically — it shipped in
  // commit 290601e alongside five unrelated surface fixes, one of which,
  // the delete-undo timing, is what ADR-077 actually documents; an earlier
  // version of this comment cited ADR-077 for this default too, which was
  // wrong.) Everything else about reading the address — the legacy
  // ?seg=explore, unknown values, Catalog's ?sub= — is in utils/libraryAddress.
  const address = resolveLibraryAddress(params)
  const seg: Seg = address.seg
  const catalogSub: CatalogSub = address.sub ?? 'decks'

  // The URL and the visible tab never disagree (ADR-106 P1): a stale or
  // unknown ?sub=, a ?sub= on Mine, or a legacy ?seg=explore is rewritten to
  // what is actually shown, in place.
  const canonical = address.canonical?.toString() ?? null
  useEffect(() => {
    if (canonical !== null) setParams(new URLSearchParams(canonical), { replace: true })
  }, [canonical, setParams])

  // The Catalog segment returns to the sub-tab it last showed this session
  // (ADR-106 P4, the owner-decisions table), so remember it whenever Catalog
  // is on screen — however it got there, deep links included.
  useEffect(() => {
    if (seg === 'catalog') writeCatalogSub(catalogSub)
  }, [seg, catalogSub])

  // Both tab levels replace the history entry rather than add one (ADR-106
  // Q3): moving within the Library is not something back retraces. Tapping
  // the tab that is already active only scrolls that stop to the top and
  // changes nothing else (Q12).
  function setSeg(s: Seg) {
    if (s === seg) { scrollAppToTop(); return }
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (s === 'mine') {
        next.set('seg', 'mine')
        next.delete('sub')
      } else {
        // Arriving at Catalog from Mine lands on the sub-tab Catalog last
        // showed this session — Decks the first time.
        next.set('seg', 'catalog')
        next.set('sub', readStopMemory().catalogSub ?? 'decks')
      }
      return next
    }, { replace: true })
  }

  function setCatalogSub(sub: CatalogSub) {
    if (seg === 'catalog' && sub === catalogSub) { scrollAppToTop(); return }
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('seg', 'catalog')
      next.set('sub', sub)
      return next
    }, { replace: true })
  }

  // ── Deck search (Mine tab only) — lifted here (out of MineDeckContent) so
  // the desktop screen-header bar can host it alongside the tabs; the input
  // that used to scroll away at the top of the page body is gone entirely on
  // desktop, never rendered in both places. Mobile is unaffected: its own
  // segment-control row above stays, unchanged, at the top of the body. ──
  // Both kept for the session (ADR-106 P4): the text in the field and the
  // search actually applied, exactly as they were left.
  const [searchInput, setSearchInput] = useState(() => stopState('mine').searchInput)
  const [search, setSearch]           = useState(() => stopState('mine').search)
  useEffect(() => { patchStopState('mine', { searchInput, search }) }, [searchInput, search])

  const handleSearchSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault()
    setSearch(searchInput)
  }, [searchInput])

  const clearSearch = useCallback(() => {
    setSearchInput('')
    setSearch('')
  }, [])

  // ── Content-column alignment for the header-bar tools (tabs + search) —
  // measured, not computed from CSS tokens alone. An earlier version of this
  // derived the column's left edge from `--sidebar-width` + `--space-10` +
  // centering math via a `calc()`, which reproduces `Layout`'s own centering
  // correctly but ignores the *other* zone sharing this row: at a narrow-ish
  // desktop width the centering margin shrinks toward zero, the computed
  // column-left lands only ~16px past the header's own 24px inset, and the
  // still-full-width "Library" identity text (which the tools zone does not
  // shrink to avoid — see the tools-zone comment on `ScreenHeaderBar`) sits
  // directly under it: the tabs painted on top of the title. Measuring the
  // identity zone's actual right edge (`#screen-header-identity`, published
  // by `ScreenHeaderBar` for exactly this) and clamping the tools' left edge
  // to never sit closer than a header-gap past it fixes that regardless of
  // title length or locale, without hardcoding how wide "Library" happens to
  // render. The content column's own left still comes from measuring this
  // page's own root (`contentColumnRef` below) rather than recomputing
  // `Layout`'s math a second time — one source of truth, read twice.
  //
  // The RIGHT edge is the content column's own right — the same edge the deck
  // cards below align to, and the only authority for it. `LibraryHeaderTools`'
  // `marginLeft: 'auto'` on the search field spends whatever is left over
  // after the tabs on the field alone, so the field occupies exactly the slack
  // between the tabs and that edge while the tabs keep their left edge.
  //
  // Two rules have been tried here and both failed by treating the bar's own
  // right inset as a second authority. The alignment pass reached the column's
  // right (correct, but read as "too short" because the bar's inset sits
  // further out and the gap between them looked unclaimed); the erratum of
  // 2026-09-10 then took `Math.max(columnRight, barRightInset)`, which made
  // the field overshoot the cards it filters by ~306px at a 1600px viewport
  // and stop belonging to them. The bar's inset is where the BAR ends; it says
  // nothing about where the content column ends, and the two diverge by
  // exactly the centring margin — i.e. by more the wider the window. Do not
  // reinstate it as a floor, a max, or a minimum width: the unclaimed strip
  // between the field and the bar's edge is the page's own margin, and it is
  // supposed to be there. If the field should instead be flush with the bar,
  // that is a change to the bar's zone rules (being redesigned separately),
  // not a second measurement here.
  //
  // Both edges come off the SAME rect, in the same pass. Deriving the right
  // edge in CSS instead (`right: calc(--space-10 + max(0px, (100vw -
  // --sidebar-width - 2*--space-10 - --page-max-width-narrow)/2))`) looks
  // equivalent and is not: `<main>` is the scrollport, so its inner width is
  // `100vw` minus the classic scrollbar, and the centring term is HALF that
  // error — the field would sit a few px off the cards on exactly the
  // platforms that show a scrollbar, which is the drift the calc-only attempt
  // above already produced once. Since the left edge has to be measured
  // regardless (it depends on the rendered width of a localized title, which
  // no `calc()` can know), reading the right off the rect that is already in
  // hand is one path used twice, not a second path. ──
  const contentColumnRef = useRef<HTMLDivElement>(null)
  const [toolsAlign, setToolsAlign] = useState<{ left: number; width: number } | null>(null)

  useLayoutEffect(() => {
    const marker = contentColumnRef.current
    if (!marker) return

    const recompute = () => {
      const markerRect = marker.getBoundingClientRect()
      const identity = document.getElementById('screen-header-identity')
      const identityRight = identity ? identity.getBoundingClientRect().right : markerRect.left
      const left = Math.max(markerRect.left, identityRight + SCREEN_HEADER_GAP)
      const width = Math.max(0, markerRect.right - left)
      setToolsAlign({ left, width })
    }

    recompute()
    // Three independent triggers, none of which alone covers every way this
    // geometry can change: window resize (viewport width), the content
    // column itself (rare — it's already capped, but covers zoom/font
    // changes), and `<main>` (id `APP_SCROLL_ID`) — the one element that
    // reliably resizes when the sidebar collapses/expands even in the common
    // case where the capped content column's own width does not change, so
    // a sidebar toggle without this would leave the tools at a stale offset.
    const ro = new ResizeObserver(recompute)
    ro.observe(marker)
    const mainEl = document.getElementById(APP_SCROLL_ID)
    if (mainEl) ro.observe(mainEl)
    const identityEl = document.getElementById('screen-header-identity')
    if (identityEl) ro.observe(identityEl)
    window.addEventListener('resize', recompute)
    return () => { ro.disconnect(); window.removeEventListener('resize', recompute) }
  }, [])

  useScreenHeader(useMemo(() => ({
    // The screen's own name — the sidebar's nav label ("Library"), not
    // `lib.title` ("My Decks"), which is the Mine tab's own in-body heading
    // and stays exactly where it is (mobile-only now; see MineDeckContent).
    // It keeps the bar's ordinary left inset unchanged: only the tools below
    // (tabs + search) are pulled off that flow to align with the content
    // column, so the screen's identity never moves.
    title: tr.nav.library,
    // `nav`, not `tools` (DESIGN_SYSTEM.md §10.3/§10.7): these tabs are how
    // the reader moves around INSIDE this screen, so they belong in the slack
    // between the bar's two ends, not in the controls group at its right end
    // — and they must never degrade into a ⋯, which is what the tools zone
    // does when the row gets tight. The search field then consumes that slack
    // to the content column's right edge, which is §10.7's own example of
    // what the slack is for: an action leaves it empty, a field consumes it,
    // navigation sits at its left end.
    //
    // No degrade tier, and none is possible here: `nav` has one rendering.
    // There was never a space shortage to degrade for — `LibraryHeaderTools`
    // sizes to the content column, which at every width this bar renders at
    // (>= the md breakpoint, per ScreenHeaderBar's own `hidden md:flex`) has
    // room for the tabs and a full search field together.
    nav: (
      <LibraryHeaderTools
        align={toolsAlign}
        activeTab={seg}
        onSelectMine={() => setSeg('mine')}
        onSelectCatalog={() => setSeg('catalog')}
        tabMineLabel={lib.tabMine}
        tabCatalogLabel={lib.tabCatalog}
        showSearch={seg === 'mine'}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onSearchSubmit={handleSearchSubmit}
        onSearchClear={clearSearch}
        searchPlaceholder={lib.searchDecks}
        searchClearLabel={lib.clearSearch}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lib, seg, searchInput, handleSearchSubmit, clearSearch, tr.nav.library, toolsAlign]))

  // ── «Добавить» (handoff §2, §7, §19.1) — owned here, not by Mine, because its
  // mobile trigger is the top bar's «+», which both tabs show. Its desktop
  // trigger is Mine's «Добавить» pill, which the popover hangs under. ──
  const navigate = useNavigate()
  const location = useLocation()
  const [showAddSheet, setShowAddSheet] = useState(false)
  const desktopAddRef = useRef<HTMLButtonElement>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showCSV, setShowCSV] = useState(false)
  const createDeck = useCreateDeck()

  function handleCreate(body: DeckCreate) {
    createDeck.mutate(body, {
      onSuccess: (deck) => {
        setShowCreate(false)
        navigate(`/decks/${deck.id}`, { state: { from: location.pathname + location.search } })
      },
      onError: (err) => {
        toast.error(extractErrorMessage(err, lib.createError))
      },
    })
  }

  return (
    <div ref={contentColumnRef} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      {/* ── Primary segment control — mobile only; desktop's tabs live in the
          screen-header bar (see useScreenHeader above), never both. The «+»
          at its right end opens «Добавить» (handoff §7). ── */}
      <div
        className="md:!hidden"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          flexShrink: 0,
        }}
      >
        <SegTab label={lib.tabMine}    active={seg === 'mine'}    onClick={() => setSeg('mine')} />
        <SegTab label={lib.tabCatalog} active={seg === 'catalog'} onClick={() => setSeg('catalog')} />
        <button
          type="button"
          onClick={() => setShowAddSheet(true)}
          aria-label={tr.addContent.title}
          aria-haspopup="dialog"
          aria-expanded={showAddSheet}
          data-testid="library-add"
          style={{
            marginLeft: 'auto', width: 44, minHeight: 44, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)',
          }}
        >
          <Plus size={22} strokeWidth={2} />
        </button>
      </div>

      {/* ── Segment content ── (the part a swipe between Library stops moves;
          the segment row above stays still — ADR-106 Q6 as revised) */}
      <div data-swipe-region="library" style={{ flex: 1, minHeight: 0 }}>
        {seg === 'mine'    && (
          <MineDeckContent
            search={search}
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
          onSearchSubmit={handleSearchSubmit}
            onClearSearch={clearSearch}
            onAdd={() => setShowAddSheet(true)}
            addOpen={showAddSheet}
            addBtnRef={desktopAddRef}
          />
        )}
        {seg === 'catalog' && <CatalogPage sub={catalogSub} onSubChange={setCatalogSub} />}
      </div>

      {/* The sheet is identical here and on Home (handoff §2); its catalog row
          opens Library → Catalog. */}
      <AddContentSheet
        open={showAddSheet}
        onClose={() => setShowAddSheet(false)}
        anchorRef={desktopAddRef}
        onCreateDeck={() => {
          setShowAddSheet(false)
          setShowCreate(true)
        }}
        onImportCSV={() => setShowCSV(true)}
      />

      {showCreate && (
        <DeckCreateModal
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
          isSaving={createDeck.isPending}
        />
      )}

      <CSVImportModal
        isOpen={showCSV}
        onClose={() => setShowCSV(false)}
      />
    </div>
  )
}

// ── Screen-header tab / search (desktop bar) ─────────────────────────────────

/**
 * The bar's tools content for Library, aligned to the deck-list content
 * column instead of the bar's own left inset.
 *
 * `ScreenHeaderBar` clusters back/identity/tools left, in normal flow, at
 * whatever width the identity zone happens to render — that is right for
 * every other screen, whose tools have no particular column to agree with,
 * but wrong here: the brief asks for the tabs to sit above the left edge of
 * the deck cards and the search to reach their right edge, and the bar's own
 * 24px inset does not land there (the page body uses a different padding
 * and a centered, capped content column — see `Layout`'s `pageMaxWidth`).
 *
 * Rather than change the bar's anatomy (which every other screen also
 * renders through) this component takes itself out of the tools zone's
 * normal flow — `position: fixed` — and is positioned by the `align` prop:
 * the content column's own measured left and right, computed once per layout
 * change by `LibraryPage` (see the long comment on its effect for why both
 * edges are measured rather than derived, and for the two rules that failed
 * before this one). This component holds no geometry of its own; it only
 * spends the width it is given — tabs at the left edge, the search field
 * taking the slack up to the right edge via its own `marginLeft: 'auto'`.
 */
function LibraryHeaderTools({
  align, activeTab, onSelectMine, onSelectCatalog, tabMineLabel, tabCatalogLabel,
  showSearch, searchValue, onSearchChange, onSearchSubmit, onSearchClear,
  searchPlaceholder, searchClearLabel,
}: {
  /** `null` before the first measurement (see the effect in `LibraryPage`) —
   * rendered invisible rather than at a guessed position, so there is never
   * a frame where the tabs flash somewhere they don't belong. */
  align: { left: number; width: number } | null
  activeTab: Seg
  onSelectMine: () => void
  onSelectCatalog: () => void
  tabMineLabel: string
  tabCatalogLabel: string
  showSearch: boolean
  searchValue: string
  onSearchChange: (v: string) => void
  onSearchSubmit: (e?: React.FormEvent) => void
  onSearchClear: () => void
  searchPlaceholder: string
  searchClearLabel: string
}) {
  const style: CSSProperties = align
    ? { ...libraryHeaderToolsAlignStyle, left: align.left, width: align.width }
    : { ...libraryHeaderToolsAlignStyle, visibility: 'hidden' }
  return (
    <div style={style}>
      <LibraryHeaderTab label={tabMineLabel} active={activeTab === 'mine'} onClick={onSelectMine} />
      <LibraryHeaderTab label={tabCatalogLabel} active={activeTab === 'catalog'} onClick={onSelectCatalog} />
      {showSearch && (
        <div style={{ flex: '1 1 auto', minWidth: 0, marginLeft: 'auto' }}>
          <LibraryHeaderSearch
            value={searchValue}
            onChange={onSearchChange}
            onSubmit={onSearchSubmit}
            onClear={onSearchClear}
            placeholder={searchPlaceholder}
            clearLabel={searchClearLabel}
          />
        </div>
      )}
    </div>
  )
}

function LibraryHeaderTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 4px',
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-ui)',
        color: active ? 'var(--text-header)' : 'var(--text-header-secondary)',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {label}
    </button>
  )
}

/**
 * A single always-expanded field, filling whatever width its wrapper gives
 * it — the wrapper (`LibraryHeaderTools`, `marginLeft: 'auto'`) is what makes
 * its right edge land on the content column's right edge, matching the tabs'
 * left edge on the other side. The icon-only collapse this used to have for
 * a `compact` bar tier is gone along with that tier: see
 * `LibraryHeaderTools`'s own comment for why there is no space shortage here
 * to collapse for.
 */
function LibraryHeaderSearch({
  value, onChange, onSubmit, onClear, placeholder, clearLabel,
}: {
  value: string
  onChange: (v: string) => void
  onSubmit: (e?: React.FormEvent) => void
  onClear: () => void
  placeholder: string
  clearLabel: string
}) {
  // A FIELD IS NOT A BUTTON (DESIGN_SYSTEM.md §10.7): it sits on
  // `--header-field` with no border, because it is somewhere you type rather
  // than something you press. Its content therefore takes the bar's own text
  // tokens (`--text-header` / `--header-field-text`), not the surface-polarity
  // ones — it is painted on the bar's plane, one alpha step up from it.
  //
  // This replaces a nested `--bg-surface` + `--border` treatment, which §8
  // recorded as a legitimate but weak application of the hairline rule: its
  // background step against the bar measured 12.68:1 in light theme and 1.13:1
  // in dark, so in dark theme the field's edge rested almost entirely on the
  // hairline. A white alpha over a plane that does not invert is the same step
  // in both themes, which is what the note was waiting for.
  return (
    <form onSubmit={onSubmit} style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
      <Search size={13} style={{ position: 'absolute', left: 10, color: 'var(--header-field-text)', pointerEvents: 'none' }} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="cs-header-field"
        style={{
          width: '100%', height: 'var(--header-control-h)', boxSizing: 'border-box',
          padding: '0 30px',
          fontSize: 13, fontFamily: 'var(--font-ui)',
          background: 'var(--header-field)', border: '1px solid transparent',
          borderRadius: 'var(--radius-pill)', color: 'var(--text-header)', outline: 'none',
        }}
        // Focus is the one state that draws an edge: the field has no border
        // at rest (§10.7), and a caret needs to say where it landed.
        onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--header-control-border-hover)')}
        onBlur={(e) => (e.currentTarget.style.borderColor = 'transparent')}
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          aria-label={clearLabel}
          style={{
            position: 'absolute', right: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--header-field-text)', display: 'flex', padding: 2,
          }}
        >
          <X size={12} />
        </button>
      )}
    </form>
  )
}

// `left`/`width` are filled in by the caller from the measured `align` prop
// (see the effect in `LibraryPage`) — this only carries the parts of the
// positioning that don't vary: taken out of the tools zone's normal flow
// entirely (`position: fixed`, viewport-relative, so the bar's own 24px
// inset and the identity zone's rendered width have no say in where this
// ends up) and pinned to the bar's own vertical band.
const libraryHeaderToolsAlignStyle: CSSProperties = {
  position: 'fixed', top: 0, height: 'var(--header-height)',
  display: 'flex', alignItems: 'center', gap: 8,
}

// ── Mine deck list ────────────────────────────────────────────────────────────

function MineDeckContent({
  search, searchInput, onSearchInputChange, onSearchSubmit, onClearSearch, onAdd, addOpen, addBtnRef,
}: {
  search: string
  searchInput: string
  onSearchInputChange: (v: string) => void
  onSearchSubmit: (e: React.FormEvent) => void
  onClearSearch: () => void
  /** Opens «Добавить», owned by LibraryPage. */
  onAdd: () => void
  addOpen: boolean
  /** The desktop «Добавить» pill: the popover hangs under it. */
  addBtnRef: RefObject<HTMLButtonElement | null>
}) {
  const navigate   = useNavigate()
  const location   = useLocation()
  const { tr } = useLocale()
  const lib = tr.library

  // ADR-106 P4: folder, sort and page come back as they were left this
  // session (the search, a level up in LibraryPage, likewise).
  const [page, setPage]               = useState(() => stopState('mine').page)
  const [folderId, setFolderId]       = useState<string | null>(() => stopState('mine').folderId)
  const [sort, setSort]               = useState<SortKey>(() => {
    const s = stopState('mine').sort
    return (SORT_KEYS as readonly string[]).includes(s) ? (s as SortKey) : 'newest'
  })
  useEffect(() => { patchStopState('mine', { page, folderId, sort }) }, [page, folderId, sort])

  // «Колоды | Тексты» (handoff §7): tap-only, remembered per device, and read
  // from one store so Mine arrives on the same segment by swipe or by tap
  // (ADR-106 Decision 8). Folder, sort, page and search below stay the Decks
  // segment's own state, kept while Texts is shown.
  const [segment, setSegment] = useMineSegment()
  const { data: textsData, isLoading: textsLoading } = useTextList()

  // Sheet / modal open states
  const [showFolders,    setShowFolders]    = useState(false)
  const [showGroupSheet, setShowGroupSheet] = useState(false)

  // Overflow dropdowns
  const [showSearchOverflow, setShowSearchOverflow] = useState(false)
  const [showFolderPicker,   setShowFolderPicker]   = useState(false)

  // Which of the two rows that can hold the sort/select control is visible:
  // the same query as Tailwind's `md` breakpoint, which shows and hides those
  // rows in CSS. Read on the first render (not in an effect) so the control is
  // never mounted in the wrong row even for one frame, and kept current when
  // the width crosses the breakpoint.
  const [isDesktopRow, setIsDesktopRow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MD_BREAKPOINT_QUERY).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(MD_BREAKPOINT_QUERY)
    setIsDesktopRow(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setIsDesktopRow(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Multi-select
  const [selectMode,   setSelectMode]   = useState(false)
  const [selectedIds,  setSelectedIds]  = useState<Set<string>>(new Set())
  // ADR-106 P3 (Decision 3.4): select mode is a mode, not an overlay, and
  // blocks top-level swipe for as long as it is on. Released when it is left
  // and, because the hook is tied to this component, when Mine unmounts.
  useSwipeNavBlock(selectMode, 'mine-select-mode')

  // Per-card overlay state
  const [cardMenuId,      setCardMenuId]      = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [moveCardId,      setMoveCardId]      = useState<string | null>(null)
  const [renameCardId,    setRenameCardId]    = useState<string | null>(null)

  const { data, isLoading, isError, refetch } = useDecks(page, PAGE_SIZE, folderId, search || undefined)
  const { data: foldersData }                 = useFolders()
  const deleteDeck   = useDeleteDeck()
  const updateDeck   = useUpdateDeck()

  const rawDecks = data?.items ?? []
  const decks    = sortDecks(rawDecks, sort)
  const total    = data?.total ?? 0
  const folders  = foldersData?.items ?? []
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1

  const activeFolderTitle = folders.find((f) => f.id === folderId)?.title ?? null

  // Exit select mode on filter/search change
  useEffect(() => {
    if (selectMode) {
      setSelectMode(false)
      setSelectedIds(new Set())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, folderId, search])

  // `search` is now committed by the desktop screen-header bar (or the
  // mobile body's own search, unaffected) one level up in LibraryPage —
  // this page still owns pagination, so it resets to page 1 whenever the
  // committed search term CHANGES. Not on mount: a page restored from this
  // session's memory (ADR-106 P4) belongs to the search restored with it.
  const prevSearch = useRef(search)
  useEffect(() => {
    if (prevSearch.current === search) return
    prevSearch.current = search
    setPage(1)
  }, [search])

  // ── ADR-106 P4: restore, checked against the server ──
  // Only a returning visit restores anything. Each remembered value is checked
  // once its data arrives and falls back on its own if the server no longer
  // has it: a deleted folder → all decks, a page past the end → the last page
  // (owner decision for P4). If anything fell back, the list is not the one
  // the remembered offset belonged to, so the stop opens at the top.
  const restoring = useRef(hasStopMemory('mine'))
  const [fellBack, setFellBack] = useState(false)
  const foldersKnown = foldersData !== undefined
  const folderValid = folderId === null || !foldersKnown || folders.some((f) => f.id === folderId)
  const pageValid = !data || page <= Math.max(1, totalPages)
  useEffect(() => {
    if (!restoring.current) return
    if (!folderValid) {
      setFolderId(null)
      setPage(1)
      setFellBack(true)
    } else if (!pageValid) {
      setPage(Math.max(1, totalPages))
      setFellBack(true)
    }
  }, [folderValid, pageValid, totalPages])
  const listReady =
    !isLoading && data !== undefined && (folderId === null || foldersKnown) && folderValid && pageValid
  useEffect(() => { if (listReady) restoring.current = false }, [listReady])
  // The stop's offset is restored once the segment on screen has its list.
  useStopScroll('mine', {
    ready: segment === 'decks' ? listReady : !textsLoading,
    restore: !fellBack,
  })

  // ── Handlers ──

  function selectSegment(next: MineSegment) {
    if (next === segment) return
    // Select mode belongs to the deck list it selects in.
    if (selectMode) exitSelectMode()
    setShowSearchOverflow(false)
    setShowFolderPicker(false)
    setSegment(next)
  }

  function startNewText() {
    useTextFlowStore.getState().start('essay', location.pathname + location.search)
    navigate('/texts/new')
  }

  function handleDelete(id: string) {
    deleteDeck.mutate(id, {
      onSuccess: () => setConfirmDeleteId(null),
    })
  }

  function handleMoveToFolder(deckId: string, targetFolderId: string | null) {
    setMoveCardId(null)
    setCardMenuId(null)
    updateDeck.mutate(
      { id: deckId, folder_id: targetFolderId },
      { onError: (err) => toast.error(extractErrorMessage(err, lib.moveError)) },
    )
  }

  function handleRename(deckId: string, title: string) {
    setRenameCardId(null)
    updateDeck.mutate(
      { id: deckId, title },
      { onError: (err) => toast.error(extractErrorMessage(err, lib.renameError)) },
    )
  }

  function enterSelectMode(deckId?: string) {
    setSelectMode(true)
    if (deckId) setSelectedIds(new Set([deckId]))
  }

  function exitSelectMode() {
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  function toggleSelect(deckId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(deckId)) next.delete(deckId)
      else next.add(deckId)
      return next
    })
  }

  const allSelected    = decks.length > 0 && decks.every((d) => selectedIds.has(d.id))
  const selectedTitles = decks.filter((d) => selectedIds.has(d.id)).map((d) => d.title)

  const hasSortActive = sort !== 'newest'

  // Sort/select overflow trigger + menu — one expression, MOUNTED in exactly
  // one of the two rows below: the row visible at the current width (mobile:
  // alongside the search form; desktop: alongside +Add, now that the title row
  // lost its title to the screen-header bar). The rows themselves are still
  // shown and hidden by Tailwind's `hidden md:flex` / `md:!hidden`.
  //
  // Mounted once, not placed in both rows with CSS hiding one: the menu
  // closes on any mousedown outside itself, so with both copies mounted a
  // press on an item of the visible copy was "outside" the hidden copy, which
  // closed the shared menu before the item's click could land — no sort
  // applied and "Select decks" did nothing, at either width. `isDesktopRow`
  // reads the same query as Tailwind's `md` breakpoint, so the control is
  // always in the row CSS is showing.
  const sortSelectOverflow = (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setShowSearchOverflow((v) => !v)}
        aria-label={lib.sortAndSelect}
        title={lib.sortAndSelect}
        style={{
          width: 42,
          height: 42,
          borderRadius: 'var(--radius-pill)',
          background: 'var(--bg-secondary)',
          border: `1px solid ${hasSortActive ? 'var(--border-active)' : 'var(--border)'}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: hasSortActive ? 'var(--accent)' : 'var(--text-secondary)',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <MoreHorizontal size={18} />
        {hasSortActive && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 7,
              right: 7,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--accent)',
            }}
          />
        )}
      </button>

      {showSearchOverflow && (
        <SearchOverflowMenu
          sort={sort}
          selectMode={selectMode}
          hasDecks={decks.length > 0}
          onSort={(s) => { setSort(s); setShowSearchOverflow(false) }}
          onEnterSelect={() => { enterSelectMode(); setShowSearchOverflow(false) }}
          onClose={() => setShowSearchOverflow(false)}
        />
      )}
    </div>
  )

  return (
    // No maxWidth/padding of its own — Layout picks this route's page
    // container width (var(--page-max-width-narrow), since Library is a
    // list that reads top-to-bottom) and already caps, centers, and insets
    // it. A second maxWidth here, redeclaring the same 680px Layout already
    // hands this route, would just be the width decided in two places again
    // instead of one — the number was never the problem, the second
    // competing declaration of it was.
    <div
      style={{
        paddingBottom: 'calc(60px + 16px)', // clear bottom nav
      }}
    >
      {/* ── First row of the body (handoff §7, §19.1): the tap-only
          «Колоды {n} | Тексты {n}» switch, scrolling with the list. It holds
          no pointer handlers and scrolls nothing sideways, so a horizontal
          drag that starts on it is still the swipe chain's (ADR-106
          Decision 4). Desktop adds «Добавить» and, for decks, sort/select at
          its right; on mobile «Добавить» is the top bar's «+». ── */}
      <div className={CONTROL_SURFACE} style={{ padding: '16px 0 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className="w-full md:w-[300px]" style={{ minWidth: 0 }} data-testid="mine-segment-switch">
          <Segmented<MineSegment>
            label={tr.textList.switchLabel}
            value={segment}
            onChange={selectSegment}
            height={isDesktopRow ? 40 : 44}
            options={[
              {
                id: 'decks',
                label: data ? `${tr.textList.segDecks} ${total}` : tr.textList.segDecks,
                testId: 'mine-seg-decks',
              },
              {
                id: 'texts',
                label: textsData ? `${tr.textList.segTexts} ${textsData.total}` : tr.textList.segTexts,
                testId: 'mine-seg-texts',
              },
            ]}
          />
        </div>

        {/* Right-aligned on desktop: «Добавить» (hidden during multi-select)
            and the decks' sort/select overflow. Grouped in one wrapper with
            the single marginLeft: 'auto' so the pair moves together. */}
        <div className="hidden md:flex" style={{ marginLeft: 'auto', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {!selectMode && (
            <button
              ref={addBtnRef}
              onClick={onAdd}
              aria-label={lib.addContent}
              aria-haspopup="dialog"
              aria-expanded={addOpen}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '7px 14px',
                background: 'var(--accent-fill)',
                color: 'var(--text-on-accent)',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'var(--font-ui)',
                cursor: 'pointer',
                minHeight: 40,
                flexShrink: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--accent-fill)')}
            >
              <Plus size={18} strokeWidth={2.5} />
              {lib.add}
            </button>
          )}

          {segment === 'decks' && (
            <div className="hidden md:flex" style={{ flexShrink: 0 }}>
              {isDesktopRow && sortSelectOverflow}
            </div>
          )}
        </div>
      </div>

      {segment === 'texts' ? (
        // Library search stays decks-only: whether it covers texts is not
        // decided (ADR-115 Decision 11).
        <TextList onAddText={startNewText} />
      ) : (<>

      {/* ── Search + overflow — mobile only; desktop's search lives in the
          screen-header bar (LibraryHeaderSearch) and its sort/select
          overflow moved up onto the row above, so this whole row has
          nothing left to do on desktop. ── */}
      <div className="md:!hidden" style={{ display: 'flex', gap: 8, marginBottom: 10, justifyContent: 'flex-end' }}>
        <form
          onSubmit={onSearchSubmit}
          className="md:!hidden"
          style={{ flex: 1, minWidth: 0, display: 'flex', position: 'relative' }}
        >
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-secondary)',
              pointerEvents: 'none',
              display: 'flex',
            }}
          >
            <Search size={15} strokeWidth={2} />
          </div>
          <input
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            placeholder={lib.searchDecks}
            aria-label={lib.searchDecks}
            style={{
              flex: 1,
              padding: '10px 36px 10px 36px',
              fontSize: 14,
              fontFamily: 'var(--font-ui)',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-pill)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--border-active)')}
            onBlur={(e)  => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
          {searchInput && (
            <button
              type="button"
              onClick={onClearSearch}
              aria-label={lib.clearSearch}
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                display: 'flex',
                padding: 4,
              }}
            >
              <X size={14} />
            </button>
          )}
        </form>

        {!isDesktopRow && sortSelectOverflow}
      </div>

      {/* ── Folder context row: chip (left) + Learn action when a folder is active ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FolderChip
          folderId={folderId}
          folderTitle={activeFolderTitle}
          open={showFolderPicker}
          onToggle={() => setShowFolderPicker((v) => !v)}
          onClose={() => setShowFolderPicker(false)}
          folders={folders}
          onSelectFolder={(id) => {
            setFolderId(id)
            setPage(1)
            setShowFolderPicker(false)
          }}
          onManageFolders={() => {
            setShowFolderPicker(false)
            setShowFolders(true)
          }}
        />

        {/* "Learn folder" — appears only when a folder is selected and not in select-mode */}
        {folderId && !selectMode && (
          <button
            onClick={() =>
              navigate(`/folders/${folderId}/learn`, {
                state: { from: location.pathname + location.search },
              })
            }
            style={{
              marginLeft: 'auto',
              padding: '7px 20px',
              background: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
              minHeight: 34,
              flexShrink: 0,
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--accent-fill)')}
          >
            {lib.learn}
          </button>
        )}
      </div>

      {/* ── Select-mode bar ── */}
      {selectMode && (
        <SelectModeBar
          selectedCount={selectedIds.size}
          allSelected={allSelected}
          onSelectAll={() => setSelectedIds(new Set(decks.map((d) => d.id)))}
          onDeselectAll={() => setSelectedIds(new Set())}
          onCancel={exitSelectMode}
          onGroup={() => setShowGroupSheet(true)}
        />
      )}

      {/* ── Deck list ── */}
      {isLoading ? (
        <DeckListSkeleton />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : decks.length === 0 ? (
        <EmptyState
          search={search}
          folderId={folderId}
          onAdd={onAdd}
        />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {decks.map((deck) => (
              <MineDeckCard
                key={deck.id}
                deck={deck}
                isSelected={selectedIds.has(deck.id)}
                selectMode={selectMode}
                folders={folders}
                cardMenuId={cardMenuId}
                setCardMenuId={setCardMenuId}
                moveCardId={moveCardId}
                setMoveCardId={setMoveCardId}
                confirmDeleteId={confirmDeleteId}
                setConfirmDeleteId={setConfirmDeleteId}
                deleteDeckPending={deleteDeck.isPending}
                renameCardId={renameCardId}
                setRenameCardId={setRenameCardId}
                renamePending={updateDeck.isPending}
                onNavigate={() =>
                  navigate(`/decks/${deck.id}`, { state: { from: location.pathname + location.search } })
                }
                onLearn={() =>
                  navigate(`/decks/${deck.id}/learn`, { state: { from: location.pathname + location.search } })
                }
                onMoveToFolder={(fid) => handleMoveToFolder(deck.id, fid)}
                onDelete={() => handleDelete(deck.id)}
                onRename={(title) => handleRename(deck.id, title)}
                onToggleSelect={() => toggleSelect(deck.id)}
                onEnterSelectMode={() => enterSelectMode(deck.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 8,
                marginTop: 24,
              }}
            >
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={paginationStyle(page === 1)}
              >
                {lib.prevPage}
              </button>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  padding: '0 8px',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={paginationStyle(page === totalPages)}
              >
                {lib.nextPage}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Mobile selection bar (fixed, above bottom nav) ── */}
      {selectMode && (
        <SelectionActionBar
          count={selectedIds.size}
          onGroup={() => setShowGroupSheet(true)}
          onCancel={exitSelectMode}
        />
      )}
      </>)}

      {/* ── Sheets and modals ── («Добавить» and what it opens are LibraryPage's) */}
      {showFolders && <FolderManagementModal onClose={() => setShowFolders(false)} />}

      {showGroupSheet && selectedIds.size > 0 && (
        <GroupIntoCourseSheet
          deckIds={Array.from(selectedIds)}
          deckTitles={selectedTitles}
          onClose={() => setShowGroupSheet(false)}
          onSuccess={exitSelectMode}
        />
      )}
    </div>
  )
}

// ── MineDeckCard ──────────────────────────────────────────────────────────────

interface MineDeckCardProps {
  deck: Deck
  isSelected: boolean
  selectMode: boolean
  folders: FolderType[]
  cardMenuId: string | null
  setCardMenuId: (id: string | null) => void
  moveCardId: string | null
  setMoveCardId: (id: string | null) => void
  confirmDeleteId: string | null
  setConfirmDeleteId: (id: string | null) => void
  deleteDeckPending: boolean
  renameCardId: string | null
  setRenameCardId: (id: string | null) => void
  renamePending: boolean
  onNavigate: () => void
  onLearn: () => void
  onMoveToFolder: (folderId: string | null) => void
  onDelete: () => void
  onRename: (title: string) => void
  onToggleSelect: () => void
  onEnterSelectMode: () => void
}

function MineDeckCard({
  deck,
  isSelected,
  selectMode,
  folders,
  cardMenuId,
  setCardMenuId,
  moveCardId,
  setMoveCardId,
  confirmDeleteId,
  setConfirmDeleteId,
  deleteDeckPending,
  renameCardId,
  setRenameCardId,
  renamePending,
  onNavigate,
  onLearn,
  onMoveToFolder,
  onDelete,
  onRename,
  onToggleSelect,
  onEnterSelectMode,
}: MineDeckCardProps) {
  const { tr } = useLocale()
  const lib = tr.library
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const didLongPress   = useRef(false)
  const menuAnchorRef  = useRef<HTMLDivElement>(null)
  const isMenuOpen     = cardMenuId === deck.id
  const isMoveOpen     = moveCardId === deck.id
  const isConfirming   = confirmDeleteId === deck.id
  const isRenaming     = renameCardId === deck.id
  const canRename      = !deck.is_system
  // ADR-106 P3: the inline delete confirmation is a flag on this card, not a
  // mounted component of its own, so the card holds its block.
  useSwipeNavBlock(isConfirming, 'mine-delete-confirm')

  // Where a pending touch long-press started; null when none is pending (never
  // started, fired, or cancelled) and for mouse presses, which movement does
  // not cancel.
  const pressStart = useRef<{ x: number; y: number } | null>(null)

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (selectMode) return
    didLongPress.current = false
    pressStart.current = e.pointerType === 'mouse' ? null : { x: e.clientX, y: e.clientY }
    longPressTimer.current = setTimeout(() => {
      pressStart.current = null
      didLongPress.current = true
      onEnterSelectMode()
      if ('vibrate' in navigator) navigator.vibrate(30)
    }, LONG_PRESS_MS)
  }, [selectMode, onEnterSelectMode])

  // A touch that moves is a scroll or a swipe, not a hold (ADR-106 P2). The
  // browser used to end the press for us by cancelling the pointer when it
  // took over a pan; once the page handles horizontal drags itself it no
  // longer does, so movement cancels the pending long-press here.
  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = pressStart.current
    if (!start) return
    if (
      Math.abs(e.clientX - start.x) > LONG_PRESS_MOVE_TOLERANCE_PX ||
      Math.abs(e.clientY - start.y) > LONG_PRESS_MOVE_TOLERANCE_PX
    ) {
      pressStart.current = null
      if (longPressTimer.current) clearTimeout(longPressTimer.current)
    }
  }, [])

  const handlePointerUp     = useCallback(() => { pressStart.current = null; if (longPressTimer.current) clearTimeout(longPressTimer.current) }, [])
  const handlePointerCancel = useCallback(() => { pressStart.current = null; if (longPressTimer.current) clearTimeout(longPressTimer.current) }, [])

  function handleCardClick() {
    if (selectMode) { onToggleSelect(); return }
    if (didLongPress.current) return
    onNavigate()
  }

  const isExcluded = !deck.is_active_for_learning
  const learned    = deck.learned_count ?? 0

  // ── Slot assembly ──────────────────────────────────────────────────────────

  const coverSlot = selectMode ? (
    <div
      aria-hidden="true"
      style={{
        width: 40, height: 40,
        borderRadius: 'var(--radius-sm)',
        background: isSelected ? 'var(--accent-fill)' : 'var(--bg-secondary)',
        border: `2px solid ${isSelected ? 'var(--accent-fill)' : 'var(--border)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        transition: 'background 0.12s, border-color 0.12s',
      }}
    >
      {isSelected && (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  ) : undefined

  const trailingSlot = !selectMode ? (
    <div ref={menuAnchorRef} style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => { setCardMenuId(isMenuOpen ? null : deck.id); setMoveCardId(null) }}
        aria-label={lib.moreOptionsFor(deck.title)}
        title={lib.moreOptions}
        style={{
          width: 32, height: 32,
          borderRadius: 'var(--radius-sm)',
          background: isMenuOpen ? 'var(--bg-secondary)' : 'transparent',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-secondary)',
          transition: 'background 0.1s',
        }}
      >
        <MoreHorizontal size={16} />
      </button>
      {isMenuOpen && (
        <CardOverflowMenu
          deck={deck}
          folders={folders}
          canDelete={!deck.is_inbox && !deck.is_system}
          canRename={canRename}
          isMoveOpen={isMoveOpen}
          anchorRef={menuAnchorRef}
          onToggleMove={() => setMoveCardId(isMoveOpen ? null : deck.id)}
          onMoveToFolder={onMoveToFolder}
          onDelete={() => { setCardMenuId(null); setConfirmDeleteId(deck.id) }}
          onRename={() => { setCardMenuId(null); setRenameCardId(deck.id) }}
          onClose={() => { setCardMenuId(null); setMoveCardId(null) }}
        />
      )}
    </div>
  ) : undefined

  const textCount = deck.text_count ?? 0
  const metaSlot = (
    <>
      {deck.due_count != null && deck.due_count > 0 && (
        <>
          <span aria-hidden="true">·</span>
          <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{deck.due_count} {lib.due}</span>
        </>
      )}
      {deck.folder_title && (
        <>
          <span aria-hidden="true">·</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Folder size={11} strokeWidth={1.75} aria-hidden="true" />
            {deck.folder_title}
          </span>
        </>
      )}
      {/* Linked texts end the meta (handoff §7). */}
      {textCount > 0 && (
        <>
          <span aria-hidden="true">·</span>
          <span data-testid="deck-text-count">{tr.textList.deckTexts(textCount)}</span>
        </>
      )}
    </>
  )

  const belowHeaderSlot = deck.card_count > 0 ? (
    <div style={{ marginTop: 12 }}>
      <DeckProgressBar learned={learned} total={deck.card_count} width="100%" height={3} />
    </div>
  ) : undefined

  const overlaySlot = isConfirming ? (
    <div
      style={{
        position: 'absolute', inset: 0,
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 12, padding: 20, zIndex: 10,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center' }}>
        {lib.confirmDelete(deck.title)}
      </p>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
        {lib.permanentlyDeleted}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setConfirmDeleteId(null)} style={confirmBtnStyle(false)}>{lib.cancel}</button>
        <button onClick={onDelete} disabled={deleteDeckPending} style={confirmBtnStyle(true)}>{lib.deleteBtn}</button>
      </div>
    </div>
  ) : isRenaming ? (
    <RenameOverlay
      title={deck.title}
      pending={renamePending}
      onCancel={() => setRenameCardId(null)}
      onSave={onRename}
    />
  ) : undefined

  return (
    <DeckCard
      deckId={deck.id}
      title={deck.title}
      cardCount={deck.card_count}
      isSystem={deck.is_system}
      coverSlot={coverSlot}
      trailingSlot={trailingSlot}
      isSelected={isSelected}
      onClick={handleCardClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      metaSlot={metaSlot}
      belowHeaderSlot={belowHeaderSlot}
      actionLabel={!selectMode ? lib.learn : undefined}
      onAction={!selectMode ? onLearn : undefined}
      bottomLeftSlot={!selectMode && isExcluded ? <DeckStatusBadge status="paused" size="sm" /> : undefined}
      overlaySlot={overlaySlot}
    />
  )
}

// ── RenameOverlay ──────────────────────────────────────────────────────────────
// Same title rules as deck creation (DeckCreateModal): trimmed, and an
// empty/whitespace-only value is refused rather than sent — the server
// enforces this independently (DeckUpdate strips and rejects a blank title
// too), this is just not making a request that can only fail.

function RenameOverlay({
  title,
  pending,
  onCancel,
  onSave,
}: {
  title: string
  pending: boolean
  onCancel: () => void
  onSave: (title: string) => void
}) {
  const { tr } = useLocale()
  const lib = tr.library
  const [value, setValue] = useState(title)
  // ADR-106 P3: mounted only while a deck is being renamed inline.
  useSwipeNavBlock(true, 'mine-inline-rename')

  function commit() {
    const trimmed = value.trim()
    if (!trimmed || trimmed === title) { onCancel(); return }
    onSave(trimmed)
  }

  return (
    <div
      style={{
        position: 'absolute', inset: 0,
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'stretch', justifyContent: 'center',
        gap: 10, padding: 20, zIndex: 10,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
        {lib.renameLabel}
      </label>
      <input
        autoFocus
        aria-label={lib.renameLabel}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // The input owns every keystroke made while it has focus — none of
          // them belong to the row underneath. Without this, a keydown here
          // bubbles to DeckCard's own onKeyDown (row keyboard activation),
          // which treats Enter/Space as "open the deck": a space typed into
          // the name was swallowed (preventDefault fired on the same event)
          // and, on Enter, the row navigated away mid-rename. Stopping
          // propagation at the source — rather than teaching the row's
          // handler to special-case a key — keeps the row's own Enter/Space
          // activation intact for every other case, since the row never sees
          // a keydown that happened inside this field at all.
          e.stopPropagation()
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') onCancel()
        }}
        maxLength={300}
        disabled={pending}
        style={{
          padding: '8px 12px', fontSize: 14, fontFamily: 'var(--font-ui)',
          background: 'var(--bg-secondary)', border: '1.5px solid var(--accent)',
          borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none',
        }}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button onClick={onCancel} disabled={pending} style={confirmBtnStyle(false)}>{lib.cancel}</button>
        <button
          onClick={commit}
          disabled={pending || !value.trim()}
          style={{
            ...confirmBtnStyle(false),
            background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none',
            opacity: pending || !value.trim() ? 0.6 : 1,
          }}
        >
          {lib.renameSave}
        </button>
      </div>
    </div>
  )
}

// ── CardOverflowMenu ──────────────────────────────────────────────────────────

function CardOverflowMenu({
  deck,
  folders,
  canDelete,
  canRename,
  isMoveOpen,
  anchorRef,
  onToggleMove,
  onMoveToFolder,
  onDelete,
  onRename,
  onClose,
}: {
  deck: Deck
  folders: FolderType[]
  canDelete: boolean
  canRename: boolean
  isMoveOpen: boolean
  anchorRef: RefObject<HTMLDivElement | null>
  onToggleMove: () => void
  onMoveToFolder: (fid: string | null) => void
  onDelete: () => void
  onRename: () => void
  onClose: () => void
}) {
  const { tr } = useLocale()
  const lib = tr.library
  const ref = useRef<HTMLDivElement>(null)
  // ADR-106 P3: mounted only while open; its move-to-folder submenu lives
  // inside it, so this one block covers both.
  useSwipeNavBlock(true, 'mine-deck-menu')
  useExclusiveOverlay(true, onClose)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [onClose])

  // Flips to open upward when there isn't room below (last row of a
  // scrolled list, a short viewport) — re-measured when the "Move to
  // folder" submenu expands the menu's own height, since that can turn a
  // menu that fit into one that no longer does.
  useFlipMenuPlacement(true, anchorRef, ref, undefined, [isMoveOpen, folders.length])

  // Rendered through a portal, not as a normal DOM child of the row: a deck
  // row (`DeckCard`) clips its own content with `overflow: hidden` for its
  // rounded corners, so a menu positioned inside it is cut off the moment it
  // is taller than the row — true whichever direction it opens, not just
  // near the bottom of the viewport. Escaping to `document.body` is what
  // `useFlipMenuPlacement` above assumes (it writes viewport-fixed
  // coordinates, not row-relative ones). React still bubbles the click
  // events below through the component tree this menu is logically part of,
  // portal or not, so the row's own `stopPropagation` (on the trailing-slot
  // wrapper) keeps working unchanged.
  if (typeof document === 'undefined') return null
  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-float)',
        minWidth: 180,
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      {/* Rename */}
      {canRename && (
        <button
          onClick={onRename}
          style={menuItemStyle}
        >
          <Pencil size={14} strokeWidth={1.75} aria-hidden="true" />
          {lib.renameDeck}
        </button>
      )}

      {/* Move to folder */}
      <button
        onClick={onToggleMove}
        style={menuItemStyle}
      >
        <Folder size={14} strokeWidth={1.75} aria-hidden="true" />
        {lib.moveToFolder}
        <ChevronDown size={12} style={{ marginLeft: 'auto', transform: isMoveOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {isMoveOpen && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <button
            onClick={() => onMoveToFolder(null)}
            style={{
              ...menuItemStyle,
              paddingLeft: 28,
              fontSize: 12,
              color: deck.folder_id === null ? 'var(--accent)' : 'var(--text-primary)',
              fontWeight: deck.folder_id === null ? 600 : 400,
            }}
          >
            {lib.noFolder} {deck.folder_id === null && '✓'}
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              onClick={() => onMoveToFolder(f.id)}
              style={{
                ...menuItemStyle,
                paddingLeft: 28,
                fontSize: 12,
                color: deck.folder_id === f.id ? 'var(--accent)' : 'var(--text-primary)',
                fontWeight: deck.folder_id === f.id ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {f.title} {deck.folder_id === f.id && '✓'}
            </button>
          ))}
          {folders.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
              {lib.noFoldersYet}
            </div>
          )}
        </div>
      )}

      {canDelete && (
        <>
          <div style={{ borderTop: '1px solid var(--border)', margin: '2px 0' }} />
          <button
            onClick={onDelete}
            style={{ ...menuItemStyle, color: 'var(--danger)' }}
          >
            {lib.deleteDeck}
          </button>
        </>
      )}
    </div>,
    document.body,
  )
}

// ── SearchOverflowMenu ────────────────────────────────────────────────────────

function SearchOverflowMenu({
  sort,
  selectMode,
  hasDecks,
  onSort,
  onEnterSelect,
  onClose,
}: {
  sort: SortKey
  selectMode: boolean
  hasDecks: boolean
  onSort: (s: SortKey) => void
  onEnterSelect: () => void
  onClose: () => void
}) {
  const { tr } = useLocale()
  const lib = tr.library
  const sortLabels: Record<SortKey, string> = {
    newest:   lib.sortNewest,
    oldest:   lib.sortOldest,
    az:       lib.sortAZ,
    za:       lib.sortZA,
    'most-due': lib.sortMostDue,
  }
  const ref = useRef<HTMLDivElement>(null)
  // ADR-106 P3: mounted only while open.
  useSwipeNavBlock(true, 'mine-sort-select-menu')
  useExclusiveOverlay(true, onClose)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [onClose])

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 6,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-float)',
        minWidth: 200,
        zIndex: 200,
        overflow: 'hidden',
      }}
    >
      {/* Multi-select */}
      {!selectMode && hasDecks && (
        <>
          <button onClick={onEnterSelect} style={menuItemStyle}>
            <CheckSquare size={14} strokeWidth={1.75} aria-hidden="true" />
            {lib.selectDecks}
          </button>
          <div style={{ borderTop: '1px solid var(--border)', margin: '2px 0' }} />
        </>
      )}

      {/* Sort options */}
      <div style={{ padding: '6px 10px 4px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {lib.sortLabel}
      </div>
      {(Object.entries(sortLabels) as [SortKey, string][]).map(([key, label]) => (
        <button
          key={key}
          onClick={() => onSort(key)}
          style={{
            ...menuItemStyle,
            color: sort === key ? 'var(--accent)' : 'var(--text-primary)',
            fontWeight: sort === key ? 600 : 400,
          }}
        >
          {label}
          {sort === key && (
            <svg style={{ marginLeft: 'auto' }} width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7L5.5 10.5L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      ))}
    </div>
  )
}

// ── FolderChip ────────────────────────────────────────────────────────────────

function FolderChip({
  folderId,
  folderTitle,
  open,
  onToggle,
  onClose,
  folders,
  onSelectFolder,
  onManageFolders,
}: {
  folderId: string | null
  folderTitle: string | null
  open: boolean
  onToggle: () => void
  onClose: () => void
  folders: FolderType[]
  onSelectFolder: (id: string | null) => void
  onManageFolders: () => void
}) {
  const { tr } = useLocale()
  const lib = tr.library
  const ref = useRef<HTMLDivElement>(null)
  // ADR-106 P3: the chip is always mounted; its picker blocks while open.
  useSwipeNavBlock(open, 'mine-folder-picker')
  useExclusiveOverlay(open, onClose)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [onClose])

  const isFiltered = folderId !== null
  const chipLabel  = folderTitle ?? lib.foldersLabel

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
      <button
        onClick={onToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          // Horizontal inset matches DeckCard's own content padding (both
          // var(--space-4)) so the chip's icon+label and the deck rows
          // below it read from the same inset — previously 12px here vs
          // 14px there, close enough to look like a mistake rather than
          // two things that were ever meant to differ.
          padding: `6px var(--space-4)`,
          background: isFiltered ? 'var(--accent-soft)' : 'var(--bg-secondary)',
          border: `1px solid ${isFiltered ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-pill)',
          fontSize: 13,
          fontWeight: isFiltered ? 600 : 400,
          fontFamily: 'var(--font-ui)',
          color: isFiltered ? 'var(--accent)' : 'var(--text-secondary)',
          cursor: 'pointer',
          minHeight: 34,
        }}
      >
        <Folder size={13} strokeWidth={1.75} aria-hidden="true" />
        {chipLabel}
        {isFiltered ? (
          <span
            role="button"
            aria-label={lib.clearFolderFilter}
            onClick={(e) => { e.stopPropagation(); onSelectFolder(null) }}
            style={{ display: 'flex', alignItems: 'center', marginLeft: 2, opacity: 0.7 }}
          >
            <X size={12} />
          </span>
        ) : (
          <ChevronDown size={12} style={{ opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 6,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-float)',
            minWidth: 200,
            zIndex: 200,
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => onSelectFolder(null)}
            style={{
              ...menuItemStyle,
              fontWeight: folderId === null ? 600 : 400,
              color:      folderId === null ? 'var(--accent)' : 'var(--text-primary)',
            }}
          >
            {lib.allDecks} {folderId === null && '✓'}
          </button>

          {folders.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', margin: '2px 0' }} />
          )}

          {folders.map((f) => (
            <button
              key={f.id}
              onClick={() => onSelectFolder(f.id)}
              style={{
                ...menuItemStyle,
                fontWeight: folderId === f.id ? 600 : 400,
                color:      folderId === f.id ? 'var(--accent)' : 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {f.title} {folderId === f.id && '✓'}
            </button>
          ))}

          <div style={{ borderTop: '1px solid var(--border)', margin: '2px 0' }} />
          <button onClick={onManageFolders} style={{ ...menuItemStyle, color: 'var(--text-secondary)', fontSize: 12 }}>
            {lib.manageFolders}
          </button>
        </div>
      )}
    </div>
  )
}

// ── SelectModeBar ─────────────────────────────────────────────────────────────

function SelectModeBar({
  selectedCount,
  allSelected,
  onSelectAll,
  onDeselectAll,
  onCancel,
  onGroup,
}: {
  selectedCount: number
  allSelected: boolean
  onSelectAll: () => void
  onDeselectAll: () => void
  onCancel: () => void
  onGroup: () => void
}) {
  const { tr } = useLocale()
  const lib = tr.library
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        padding: '10px 14px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        flexWrap: 'wrap',
      }}
    >
      <button
        onClick={allSelected ? onDeselectAll : onSelectAll}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'var(--font-ui)',
          padding: 0,
        }}
      >
        {allSelected ? <CheckSquare size={15} /> : <Square size={15} />}
        {allSelected ? lib.deselectAll : lib.selectAll}
      </button>

      <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
        {selectedCount > 0 ? lib.selected(selectedCount) : lib.tapToSelect}
      </span>

      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        {selectedCount > 0 && (
          <button
            onClick={onGroup}
            style={{
              padding: '7px 14px',
              background: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
            }}
          >
            {lib.groupIntoCourse}
          </button>
        )}
        <button
          onClick={onCancel}
          style={{
            padding: '7px 12px',
            background: 'transparent',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-pill)',
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <X size={13} /> {lib.cancel}
        </button>
      </div>
    </div>
  )
}

// ── SelectionActionBar (fixed, above bottom nav, mobile-only via class) ───────

function SelectionActionBar({
  count,
  onGroup,
  onCancel,
}: {
  count: number
  onGroup: () => void
  onCancel: () => void
}) {
  const { tr } = useLocale()
  const lib = tr.library
  return (
    <div
      className="flex md:hidden"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 60,
        zIndex: 150,
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '12px 16px',
        background: 'var(--bg-surface)',
        borderTop: '1px solid var(--border)',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
      }}
    >
      <span
        style={{
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'var(--font-ui)',
          color: count > 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
        }}
      >
        {count > 0 ? lib.decksSelected(count) : lib.tapDeck}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        {count > 0 && (
          <button
            onClick={onGroup}
            style={{
              padding: '10px 18px',
              background: 'var(--accent-fill)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            {lib.groupIntoCourse}
          </button>
        )}
        <button
          onClick={onCancel}
          style={{
            padding: '10px 14px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-pill)',
            fontSize: 14,
            cursor: 'pointer',
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({
  search,
  folderId,
  onAdd,
}: {
  search: string
  folderId: string | null
  onAdd: () => void
}) {
  const { tr } = useLocale()
  const lib = tr.library
  if (search || folderId) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '60px 0',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
          {lib.noDecksFound}
        </p>
        <p style={{ fontSize: 13, margin: 0 }}>
          {search ? lib.noDecksMatch(search) : lib.noDecksInFolder}
        </p>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 24 }}>
      {/* Create-first-deck placeholder — the only dashed element per spec */}
      <button
        onClick={onAdd}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          width: '100%',
          padding: '40px 24px',
          background: 'transparent',
          border: '2px dashed var(--border)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-ui)',
          transition: 'border-color 0.15s, color 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--accent)'
          e.currentTarget.style.color = 'var(--accent)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)'
          e.currentTarget.style.color = 'var(--text-secondary)'
        }}
      >
        <Plus size={28} strokeWidth={1.5} />
        <div>
          <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: 'inherit' }}>
            {lib.addFirstDeck}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            {lib.addFirstDeckDesc}
          </p>
        </div>
      </button>
    </div>
  )
}

// ── Skeletons / error ─────────────────────────────────────────────────────────

function DeckListSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          style={{
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            padding: 14,
            height: 100,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--bg-secondary)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 14, background: 'var(--bg-secondary)', borderRadius: 4, width: '65%', marginBottom: 8 }} />
            <div style={{ height: 12, background: 'var(--bg-secondary)', borderRadius: 4, width: '35%' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { tr } = useLocale()
  const lib = tr.library
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '60px 0',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
        {lib.couldNotLoadDecks}
      </p>
      <p style={{ margin: '0 0 20px', fontSize: 13 }}>
        {lib.checkConnection}
      </p>
      <button
        onClick={onRetry}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '10px 24px',
          background: 'var(--accent-fill)',
          color: 'var(--text-on-accent)',
          border: 'none',
          borderRadius: 'var(--radius-pill)',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'var(--font-ui)',
          cursor: 'pointer',
        }}
      >
        <RefreshCw size={14} /> {lib.retry}
      </button>
    </div>
  )
}

// ── Segment tab ───────────────────────────────────────────────────────────────

function SegTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '11px 22px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 15,
        fontWeight: 600,
        fontFamily: 'var(--font-ui)',
        color: active ? 'var(--accent)' : 'var(--text-secondary)',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
        transition: 'color 0.15s, border-color 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  )
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  textAlign: 'left',
  padding: '9px 14px',
  fontSize: 13,
  fontFamily: 'var(--font-ui)',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  color: 'var(--text-primary)',
}

function confirmBtnStyle(destructive: boolean): React.CSSProperties {
  return {
    padding: '8px 20px',
    background: destructive ? 'var(--danger-fill)' : 'var(--bg-secondary)',
    color: destructive ? 'var(--text-on-dark)' : 'var(--text-primary)',
    border: destructive ? 'none' : '1px solid var(--border)',
    borderRadius: 'var(--radius-pill)',
    fontSize: 13,
    fontFamily: 'var(--font-ui)',
    fontWeight: 600,
    cursor: 'pointer',
    minWidth: 80,
  }
}

function paginationStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '8px 18px',
    background: disabled ? 'var(--bg-secondary)' : 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-pill)',
    fontSize: 13,
    fontFamily: 'var(--font-ui)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    color: 'var(--text-primary)',
  }
}
